import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import MDSpinner from "react-md-spinner";
import {
  FaChevronDown,
  FaInfoCircle,
  FaTools,
  FaWrench,
} from "react-icons/fa";
import { eventLabel, formatDetailValue } from "./fieldAppDetailLabels";
import { formatActivityDuration } from "./GraphActivityLanesLayer";
import { fuelStatsForRange } from "./FieldAppActivityTimeline";
import {
  fetchMachinePeriodSummary,
  recalcTelemetryAreaHa,
  updateSessionFinishReadings,
  updateWorkingWidth,
} from "./fieldAppApi";
import {
  buildCustomPeriod,
  buildLastMonthPeriod,
  buildLastWeekPeriod,
  buildSinceDayPeriod,
  DETAIL_SCOPE,
  readStoredSinceStartDay,
  writeStoredSinceStartDay,
} from "./fieldAppPeriodUtils";
import {
  formatKm,
  METER_BOTH,
  METER_HOUR,
  resolveDisplayedPanelKm,
  resolveDisplayedTelemetryKm,
  resolveMeterPresentation,
  resolvePanelReadingValue,
} from "./fieldAppMeterLabels";

function formatNumber(value, fractionDigits = 2) {
  if (value == null || Number.isNaN(Number(value))) return "—";
  return Number(value).toLocaleString("pt-BR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: fractionDigits,
  });
}

function formatHours(value) {
  if (value == null || Number.isNaN(Number(value))) return "—";
  return `${formatNumber(value)} h`;
}

function formatPercent(value) {
  if (value == null || Number.isNaN(Number(value))) return "—";
  return `${formatNumber(value, 1)}%`;
}

function formatLocation(location) {
  if (!location || location.latitude == null || location.longitude == null) {
    return "—";
  }
  return `${Number(location.latitude).toFixed(6)}, ${Number(location.longitude).toFixed(6)}`;
}

function toDayKey(ms) {
  if (ms == null) return "";
  const date = new Date(ms);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (value) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function SessionColumn({ title, hint, children, className }) {
  if (!children) return null;
  return (
    <section
      className={`field-app-activity-session-column${
        className ? ` ${className}` : ""
      }`}
    >
      <h4 className="field-app-activity-session-column-title">{title}</h4>
      {hint ? (
        <p className="field-app-activity-session-column-hint">{hint}</p>
      ) : null}
      <div className="field-app-activity-session-column-body">{children}</div>
    </section>
  );
}

function MetricInfoButton({ info, label }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const handleDocumentClick = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleDocumentClick);
    return () => document.removeEventListener("mousedown", handleDocumentClick);
  }, [open]);

  if (!info) return null;

  return (
    <span className="field-app-activity-session-info-wrap" ref={rootRef}>
      <button
        type="button"
        className={`field-app-activity-session-info-btn${
          open ? " is-open" : ""
        }`}
        aria-expanded={open}
        aria-label={label ? `${label}. ${info}` : info}
        onClick={(event) => {
          event.stopPropagation();
          setOpen((current) => !current);
        }}
      >
        <FaInfoCircle aria-hidden />
      </button>
      {open ? (
        <span
          className="field-app-activity-session-info-popover"
          role="tooltip"
        >
          {info}
        </span>
      ) : null}
    </span>
  );
}

function WorkingWidthInlineEdit({ valueM, comparison, farmId, operationId, t, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);
  const canEdit =
    comparison &&
    (comparison.equipmentId ||
      comparison.monitoringOperationId ||
      operationId);

  useEffect(() => {
    if (!editing || !inputRef.current) return;
    inputRef.current.focus();
    inputRef.current.select();
  }, [editing]);

  const beginEdit = () => {
    if (!canEdit) return;
    setDraft(valueM != null ? String(valueM) : "");
    setError(null);
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setError(null);
  };

  const saveWidth = async () => {
    const parsed = Number.parseFloat(String(draft).replace(",", "."));
    if (!parsed || parsed <= 0) {
      setError(t("Informe uma largura válida em metros."));
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const nextAreaHa = await updateWorkingWidth(
        farmId,
        comparison,
        parsed,
        operationId
      );
      onSaved({
        workingWidthM: parsed,
        telemetryAreaHa: nextAreaHa,
      });
      setEditing(false);
    } catch (saveError) {
      setError(t("Não foi possível salvar a largura."));
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <span className="field-app-activity-session-width-edit">
        <input
          ref={inputRef}
          type="number"
          min="0.1"
          step="0.1"
          className="field-app-activity-session-width-input"
          value={draft}
          disabled={saving}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              saveWidth();
            }
            if (event.key === "Escape") {
              event.preventDefault();
              cancelEdit();
            }
          }}
        />
        <span className="field-app-activity-session-width-unit">m</span>
        {saving ? (
          <MDSpinner singleColor="#1ABB9C" size={16} />
        ) : null}
        {error ? (
          <span className="field-app-activity-session-width-error">{error}</span>
        ) : null}
      </span>
    );
  }

  if (!canEdit) {
    return (
      <strong className="field-app-activity-session-metric-value">
        {valueM != null ? `${formatNumber(valueM, 1)} m` : "—"}
      </strong>
    );
  }

  return (
    <button
      type="button"
      className="field-app-activity-session-width-trigger"
      onClick={beginEdit}
      title={t("Clique para alterar a largura de trabalho")}
    >
      <strong>{formatNumber(valueM, 1)} m</strong>
      <FaChevronDown aria-hidden />
    </button>
  );
}

function MetricRow({ label, value, emphasize, info, action, valueNode }) {
  if (!valueNode && (value == null || value === "" || value === "—")) return null;
  return (
    <div
      className={
        emphasize
          ? "field-app-activity-session-metric field-app-activity-session-metric--emphasis"
          : "field-app-activity-session-metric"
      }
    >
      <span className="field-app-activity-session-metric-label">
        <span>{label}</span>
        <MetricInfoButton info={info} label={label} />
        {action || null}
      </span>
      {valueNode || (
        <strong className="field-app-activity-session-metric-value">{value}</strong>
      )}
    </div>
  );
}

function SessionSnapshot({ snapshot, formatTimestamp, t, compact, meterPresentation }) {
  if (!snapshot) return null;

  const hourReading =
    meterPresentation.snapshotHourLabel != null
      ? meterPresentation.meterType === METER_BOTH
        ? snapshot.hourMeter
        : resolvePanelReadingValue(snapshot, METER_HOUR)
      : null;
  const odometerReading =
    meterPresentation.snapshotOdometerLabel != null
      ? meterPresentation.meterType === METER_BOTH
        ? snapshot.odometer
        : resolvePanelReadingValue(snapshot, meterPresentation.meterType)
      : null;

  const rows = [
    {
      label: t("Horário"),
      value: formatTimestamp(new Date(snapshot.timestamp).getTime()),
    },
    meterPresentation.snapshotHourLabel
      ? {
          label: meterPresentation.snapshotHourLabel,
          value:
            hourReading != null
              ? `${formatNumber(hourReading)} h`
              : null,
        }
      : null,
    meterPresentation.snapshotOdometerLabel
      ? {
          label: meterPresentation.snapshotOdometerLabel,
          value:
            odometerReading != null ? formatKm(odometerReading) : null,
        }
      : null,
    {
      label: t("Hectares no painel"),
      value:
        snapshot.panelHectares != null
          ? `${formatNumber(snapshot.panelHectares)} ha`
          : null,
    },
    {
      label: t("Identificação"),
      value: snapshot.machineName,
    },
    {
      label: t("Localização"),
      value: formatLocation(snapshot.location),
      hide: compact,
    },
  ].filter(Boolean);

  return (
    <>
      {snapshot.remoteFinish ? (
        <p className="field-app-activity-session-remote-finish">
          {t("Encerramento à distância — leituras podem estar pendentes.")}
        </p>
      ) : null}
      {rows.map((row) =>
        row.hide || row.value == null ? null : (
          <MetricRow key={row.label} label={row.label} value={row.value} />
        )
      )}
    </>
  );
}

function MaintenanceCard({ item, formatTimestamp, t }) {
  const Icon =
    item.eventType === "MANUTENCAO_EXECUTADA" ? FaTools : FaWrench;

  return (
    <div className="field-app-activity-session-maintenance">
      <div className="field-app-activity-session-maintenance-head">
        <Icon aria-hidden />
        <strong>{eventLabel(item.eventType)}</strong>
        <span>{formatTimestamp(new Date(item.timestamp).getTime())}</span>
      </div>
      {item.description ? (
        <p className="field-app-activity-session-maintenance-desc">
          {item.description}
        </p>
      ) : null}
      <div className="field-app-activity-session-maintenance-meta">
        {item.hourMeter != null ? (
          <span>
            {t("Horímetro")}: {formatNumber(item.hourMeter)} h
          </span>
        ) : null}
        {item.targetHourMeter != null ? (
          <span>
            {t("Horímetro alvo")}: {formatNumber(item.targetHourMeter)} h
          </span>
        ) : null}
        {item.hoursUntilNext != null ? (
          <span>
            {t("Próxima em")}: {formatNumber(item.hoursUntilNext)} h
          </span>
        ) : null}
        {item.observations ? (
          <span>{formatDetailValue("observacoes", item.observations)}</span>
        ) : null}
      </div>
    </div>
  );
}

function MachineClosureEditor({
  sessionDetail,
  farmId,
  formatTimestamp,
  t,
  onSaved,
  chartPickActive,
  onStartChartPick,
  onStopChartPick,
  selectedChartPoint,
  onClearChartPoint,
}) {
  const [editorOpen, setEditorOpen] = useState(false);
  const [hourDraft, setHourDraft] = useState("");
  const [odometerDraft, setOdometerDraft] = useState("");
  const [noteDraft, setNoteDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const needsAttention =
    sessionDetail &&
    (sessionDetail.meterReadingsPending || sessionDetail.remoteFinish);

  if (!needsAttention) return null;

  const saveClosure = async () => {
    const hourMeter =
      hourDraft.trim() === ""
        ? null
        : Number.parseFloat(String(hourDraft).replace(",", "."));
    const odometer =
      odometerDraft.trim() === ""
        ? null
        : Number.parseFloat(String(odometerDraft).replace(",", "."));

    if (
      hourMeter == null &&
      odometer == null &&
      !selectedChartPoint
    ) {
      setError(
        t(
          "Informe horímetro/hodômetro ou clique no gráfico para marcar o encerramento."
        )
      );
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await updateSessionFinishReadings(farmId, {
        sessionUuid: sessionDetail.sessionUuid,
        hourMeter,
        odometer,
        managerNote: noteDraft.trim() || null,
        finishTimestamp: selectedChartPoint
          ? new Date(selectedChartPoint.time).toISOString()
          : null,
        finishLatitude: selectedChartPoint ? selectedChartPoint.lat : null,
        finishLongitude: selectedChartPoint ? selectedChartPoint.lng : null,
      });
      onStopChartPick();
      onSaved();
      setEditorOpen(false);
    } catch (saveError) {
      setError(t("Não foi possível salvar o encerramento."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="field-app-activity-closure-editor">
      <div className="field-app-activity-closure-editor-head">
        <div>
          <strong>{t("Encerramento da máquina")}</strong>
          {sessionDetail.meterReadingsPending ? (
            <span className="field-app-activity-closure-badge">
              {t("Pendente")}
            </span>
          ) : null}
        </div>
        <button
          type="button"
          className={
            editorOpen
              ? "btn btn-xs btn-default"
              : "btn btn-xs btn-warning"
          }
          onClick={() => setEditorOpen((current) => !current)}
        >
          {editorOpen
            ? t("Fechar editor")
            : t("Editar encerramento da máquina")}
        </button>
      </div>

      {sessionDetail.finishObservation ? (
        <p className="field-app-activity-session-operator-note">
          <span>{t("Observação do operador")}: </span>
          {sessionDetail.finishObservation}
        </p>
      ) : null}

      {sessionDetail.finishObservationAudioUrl ? (
        <p className="field-app-activity-session-operator-note">
          <span>{t("Áudio do operador")}: </span>
          <audio controls preload="none" src={sessionDetail.finishObservationAudioUrl}>
            {t("Seu navegador não reproduz áudio.")}
          </audio>
        </p>
      ) : null}

      {editorOpen ? (
        <div className="field-app-activity-session-pending-readings-form">
          <p className="field-app-activity-closure-hint">
            {t(
              "Clique no gráfico acima no último ponto com velocidade (onde a máquina parou). O mapa será centralizado nesse instante."
            )}
          </p>
          <div className="field-app-activity-closure-chart-actions">
            {!chartPickActive ? (
              <button
                type="button"
                className="btn btn-xs btn-primary"
                onClick={onStartChartPick}
              >
                {t("Escolher ponto no gráfico")}
              </button>
            ) : (
              <button
                type="button"
                className="btn btn-xs btn-default"
                onClick={onStopChartPick}
              >
                {t("Cancelar seleção no gráfico")}
              </button>
            )}
            {selectedChartPoint ? (
              <>
                <span className="field-app-activity-closure-chart-point">
                  {t("Ponto")}: {formatTimestamp(selectedChartPoint.time)} ·{" "}
                  {selectedChartPoint.speed || "—"}
                </span>
                <button
                  type="button"
                  className="btn btn-xs btn-link"
                  onClick={onClearChartPoint}
                >
                  {t("Limpar ponto")}
                </button>
              </>
            ) : null}
          </div>
          <label>
            {t("Horímetro final (h)")}
            <input
              type="number"
              step="0.1"
              min="0"
              value={hourDraft}
              disabled={saving}
              onChange={(event) => setHourDraft(event.target.value)}
            />
          </label>
          <label>
            {t("Hodômetro final (km)")}
            <input
              type="number"
              step="0.1"
              min="0"
              value={odometerDraft}
              disabled={saving}
              onChange={(event) => setOdometerDraft(event.target.value)}
            />
          </label>
          <label>
            {t("Anotação do gestor (opcional)")}
            <textarea
              rows={2}
              value={noteDraft}
              disabled={saving}
              onChange={(event) => setNoteDraft(event.target.value)}
            />
          </label>
          {error ? (
            <p className="field-app-activity-session-width-error">{error}</p>
          ) : null}
          <button type="button" disabled={saving} onClick={saveClosure}>
            {saving ? t("Salvando…") : t("Salvar encerramento")}
          </button>
        </div>
      ) : null}
    </section>
  );
}

function FuelColumn({ fuel, t }) {
  if (!fuel || !fuel.recordCount) {
    return (
      <SessionColumn title={t("Combustível")}>
        <p className="field-app-activity-session-empty">
          {t("Nenhum abastecimento no período analisado.")}
        </p>
      </SessionColumn>
    );
  }

  return (
    <SessionColumn title={t("Combustível")}>
      <MetricRow
        label={t("Total abastecido")}
        value={`${formatNumber(fuel.totalLiters)} L`}
        emphasize
      />
      <MetricRow
        label={t("Abastecimentos")}
        value={String(fuel.recordCount)}
      />
      <MetricRow
        label={t("Média por parada")}
        value={`${formatNumber(fuel.avgLitersPerStop)} L`}
      />
    </SessionColumn>
  );
}

function TelemetryComparisonColumn({
  comparison,
  farmId,
  operationId,
  isIntervalScope,
  meterPresentation,
  t,
}) {
  const [localComparison, setLocalComparison] = useState(comparison);

  useEffect(() => {
    setLocalComparison(comparison);
  }, [comparison]);

  const handleWidthSaved = useCallback((patch) => {
    setLocalComparison((current) => ({
      ...(current || {}),
      ...patch,
      telemetryAreaHa:
        patch.telemetryAreaHa != null
          ? patch.telemetryAreaHa
          : recalcTelemetryAreaHa(
              current && current.pathDistanceKm,
              patch.workingWidthM
            ),
    }));
  }, []);

  if (!localComparison) {
    return (
      <SessionColumn
        title={t("Comparativo telemetria")}
        hint={t(
          "Sem pontos GPS no turno para calcular movimento, paradas e área estimada."
        )}
      >
        <p className="field-app-activity-session-empty">
          {t("Nenhum ponto de telemetria neste período.")}
        </p>
      </SessionColumn>
    );
  }

  const dataSourceLabel =
    localComparison.dataSource === "machine_gps"
      ? t("GPS da máquina")
      : localComparison.dataSource === "operator_gps"
        ? t("GPS do operador (fallback)")
        : t("Sem GPS");

  const stoppedTimeInfo =
    isIntervalScope || !localComparison.scopedToMachineSessions
      ? t(
          "Parado segundo o GPS (velocidade baixa). Paradas de 5 minutos ou mais entram neste total. Diferente da marcha lenta do painel."
        )
      : t(
          "Parado segundo o GPS, somente nos turnos assumidos. Paradas de 5 minutos ou mais entram neste total. Diferente da marcha lenta do painel."
        );

  const idleEngineInfo =
    localComparison.idleEnginePct != null
      ? t(
          "Horas do painel menos tempo em movimento pelo GPS: motor ligado sem deslocamento efetivo. Neste turno: {{pct}}% das horas reais no painel.",
          { pct: formatNumber(localComparison.idleEnginePct, 1) }
        )
      : t(
          "Horas do painel menos tempo em movimento pelo GPS. Representa motor ligado sem deslocamento efetivo no turno."
        );

  const idleEngineValue = formatHours(localComparison.idleEngineHours);
  const displayedTelemetryKm = resolveDisplayedTelemetryKm({
    odometerKm: localComparison.odometerKm,
    odometerKmFromGps: localComparison.odometerKmFromGps,
    pathDistanceKm: localComparison.pathDistanceKm,
    meterPresentation,
    t,
  });

  const telemetryAreaInfo =
    isIntervalScope || !localComparison.scopedToMachineSessions
      ? t(
          "Estimativa pelo deslocamento total GPS no turno multiplicado pela largura configurada da máquina. Considera todo o percurso do período, incluindo repasses (trechos percorridos mais de uma vez)."
        )
      : t(
          "Estimativa pelo deslocamento GPS somado em todos os turnos assumidos no período, multiplicado pela largura configurada da máquina. Inclui repasses."
        );

  const widthInfo = t(
    "Largura usada no cálculo da área estimada. Clique no valor para ajustar aqui mesmo, sem abrir outra página."
  );

  return (
    <SessionColumn title={t("Comparativo telemetria")}>
      <MetricRow label={t("Fonte")} value={dataSourceLabel} />
      <MetricRow
        label={t("Tempo em movimento")}
        value={formatHours(localComparison.inMotionHours)}
        emphasize
      />
      <MetricRow
        label={t("Tempo parado (GPS)")}
        value={formatHours(localComparison.stoppedHours)}
        info={stoppedTimeInfo}
      />
      {meterPresentation.usesHours && localComparison.hourMeterHours != null ? (
        <MetricRow
          label={meterPresentation.panelRealLabel}
          value={formatHours(localComparison.hourMeterHours)}
          emphasize
          info={meterPresentation.panelWorkedHint}
        />
      ) : null}
      {displayedTelemetryKm ? (
        <MetricRow
          label={displayedTelemetryKm.label}
          value={formatKm(displayedTelemetryKm.value)}
          emphasize
          info={displayedTelemetryKm.hint}
        />
      ) : null}
      {meterPresentation.showIdleEngine ? (
        <MetricRow
          label={t("Marcha lenta (painel)")}
          value={idleEngineValue}
          emphasize
          info={idleEngineInfo}
        />
      ) : null}
      <MetricRow
        label={t("Hectares estimados (telemetria)")}
        value={
          localComparison.telemetryAreaHa != null
            ? `${formatNumber(localComparison.telemetryAreaHa)} ha`
            : null
        }
        emphasize
        info={telemetryAreaInfo}
      />
      <MetricRow
        label={t("Percurso GPS")}
        value={
          localComparison.pathDistanceKm != null
            ? `${formatNumber(localComparison.pathDistanceKm)} km`
            : null
        }
      />
      <MetricRow
        label={t("Largura configurada")}
        info={widthInfo}
        valueNode={
          localComparison.workingWidthM != null ? (
            <WorkingWidthInlineEdit
              valueM={localComparison.workingWidthM}
              comparison={localComparison}
              farmId={farmId}
              operationId={operationId}
              t={t}
              onSaved={handleWidthSaved}
            />
          ) : (
            <strong className="field-app-activity-session-metric-value">—</strong>
          )
        }
      />
    </SessionColumn>
  );
}

function DetailScopeToolbar({
  scopeMode,
  onScopeChange,
  sinceStartDate,
  onSinceStartDateChange,
  onSinceDayApply,
  customDateFrom,
  customDateTo,
  onCustomDateFromChange,
  onCustomDateToChange,
  onApplyCustom,
  t,
}) {
  const sinceActive = scopeMode === DETAIL_SCOPE.SINCE_DAY;

  return (
    <div className="field-app-activity-session-scope">
      <span className="field-app-activity-session-scope-label">
        {t("Período analisado")}:
      </span>
      <div className="field-app-activity-session-scope-buttons">
        <button
          type="button"
          className={
            scopeMode === DETAIL_SCOPE.INTERVAL
              ? "btn btn-xs btn-primary"
              : "btn btn-xs btn-default"
          }
          onClick={() => onScopeChange(DETAIL_SCOPE.INTERVAL)}
        >
          {t("Este trecho")}
        </button>
        <div
          className={
            sinceActive
              ? "field-app-activity-session-since-control field-app-activity-session-since-control--active"
              : "field-app-activity-session-since-control"
          }
        >
          <button
            type="button"
            className={
              sinceActive ? "btn btn-xs btn-primary" : "btn btn-xs btn-default"
            }
            onClick={() => onScopeChange(DETAIL_SCOPE.SINCE_DAY)}
          >
            {t("Desde o dia")}
          </button>
          <input
            type="date"
            value={sinceStartDate}
            onChange={(event) => onSinceStartDateChange(event.target.value)}
            aria-label={t("Data inicial do período")}
          />
          <button
            type="button"
            className="btn btn-xs btn-default field-app-activity-session-since-apply"
            onClick={onSinceDayApply}
            title={t("Atualizar resumo desde a data escolhida")}
          >
            {t("Ir")}
          </button>
        </div>
        <button
          type="button"
          className={
            scopeMode === DETAIL_SCOPE.WEEK
              ? "btn btn-xs btn-primary"
              : "btn btn-xs btn-default"
          }
          onClick={() => onScopeChange(DETAIL_SCOPE.WEEK)}
        >
          {t("Última semana")}
        </button>
        <button
          type="button"
          className={
            scopeMode === DETAIL_SCOPE.MONTH
              ? "btn btn-xs btn-primary"
              : "btn btn-xs btn-default"
          }
          onClick={() => onScopeChange(DETAIL_SCOPE.MONTH)}
        >
          {t("Último mês")}
        </button>
        <button
          type="button"
          className={
            scopeMode === DETAIL_SCOPE.CUSTOM
              ? "btn btn-xs btn-primary"
              : "btn btn-xs btn-default"
          }
          onClick={() => onScopeChange(DETAIL_SCOPE.CUSTOM)}
        >
          {t("Personalizado")}
        </button>
      </div>
      {scopeMode === DETAIL_SCOPE.CUSTOM ? (
        <div className="field-app-activity-session-scope-custom">
          <label>
            <span>{t("De")}</span>
            <input
              type="date"
              value={customDateFrom}
              onChange={(event) => onCustomDateFromChange(event.target.value)}
            />
          </label>
          <label>
            <span>{t("Até")}</span>
            <input
              type="date"
              value={customDateTo}
              onChange={(event) => onCustomDateToChange(event.target.value)}
            />
          </label>
          <button
            type="button"
            className="btn btn-xs btn-success"
            onClick={onApplyCustom}
          >
            {t("Aplicar")}
          </button>
        </div>
      ) : null}
    </div>
  );
}

export default function FieldAppActivitySessionDetail({
  selection,
  formatTimestamp,
  onClose,
  farmId,
  operatorIds = [],
  fuelRecords = [],
  defaultSinceDay = "",
  operationId = null,
  chartPickActive = false,
  onStartChartPick,
  onStopChartPick,
  selectedChartPoint = null,
  onClearChartPoint,
}) {
  const { t } = useTranslation();
  const [scopeMode, setScopeMode] = useState(DETAIL_SCOPE.INTERVAL);
  const [sinceStartDate, setSinceStartDate] = useState(() =>
    readStoredSinceStartDay(
      farmId,
      defaultSinceDay || (selection ? toDayKey(selection.startMs) : "")
    )
  );
  const [sinceAppliedKey, setSinceAppliedKey] = useState("");
  const [customDateFrom, setCustomDateFrom] = useState("");
  const [customDateTo, setCustomDateTo] = useState("");
  const [customAppliedKey, setCustomAppliedKey] = useState("");
  const [periodSummary, setPeriodSummary] = useState(null);
  const [periodLoading, setPeriodLoading] = useState(false);
  const [periodError, setPeriodError] = useState(null);
  const [closureSavedToken, setClosureSavedToken] = useState(0);

  const selectionKey = selection
    ? `${selection.lane.id}-${selection.startMs}-${selection.endMs}`
    : null;

  useEffect(() => {
    if (!selection) return;
    setScopeMode(DETAIL_SCOPE.INTERVAL);
    setPeriodSummary(null);
    setPeriodError(null);
    setCustomDateFrom(toDayKey(selection.startMs));
    setCustomDateTo(toDayKey(selection.endMs));
    setCustomAppliedKey("");
    setSinceAppliedKey("");
    setClosureSavedToken(0);
    setSinceStartDate((current) =>
      readStoredSinceStartDay(
        farmId,
        defaultSinceDay || toDayKey(selection.startMs) || current
      )
    );
  }, [selectionKey, selection, farmId, defaultSinceDay]);

  const loadPeriodSummary = useCallback(
    async (mode, options = {}) => {
      if (!selection || !farmId || selection.lane.type !== "machine") {
        return;
      }

      let period = null;
      if (mode === DETAIL_SCOPE.WEEK) {
        period = buildLastWeekPeriod();
      } else if (mode === DETAIL_SCOPE.MONTH) {
        period = buildLastMonthPeriod();
      } else if (mode === DETAIL_SCOPE.SINCE_DAY) {
        const dayKey = options.sinceStartDate;
        if (!dayKey) return;
        period = buildSinceDayPeriod(dayKey);
      } else if (mode === DETAIL_SCOPE.CUSTOM) {
        const { customFrom, customTo } = options;
        if (!customFrom || !customTo) return;
        period = buildCustomPeriod(customFrom, customTo);
      } else {
        return;
      }

      setPeriodLoading(true);
      setPeriodError(null);
      try {
        const data = await fetchMachinePeriodSummary(farmId, {
          operatorIds,
          machineIdent: selection.lane.label,
          dateFrom: period.dateFrom,
          dateTo: period.dateTo,
        });
        setPeriodSummary(data);
      } catch (error) {
        setPeriodSummary(null);
        setPeriodError(
          t("Não foi possível carregar o resumo do período selecionado.")
        );
      } finally {
        setPeriodLoading(false);
      }
    },
    [farmId, operatorIds, selection, t]
  );

  useEffect(() => {
    if (scopeMode === DETAIL_SCOPE.INTERVAL) {
      setPeriodSummary(null);
      setPeriodError(null);
      return;
    }
    if (scopeMode === DETAIL_SCOPE.CUSTOM) {
      if (!customAppliedKey) return;
      const [from, to] = customAppliedKey.split("|");
      loadPeriodSummary(DETAIL_SCOPE.CUSTOM, {
        customFrom: from,
        customTo: to,
      });
      return;
    }
    if (scopeMode === DETAIL_SCOPE.SINCE_DAY) {
      if (!sinceAppliedKey) return;
      loadPeriodSummary(DETAIL_SCOPE.SINCE_DAY, {
        sinceStartDate: sinceAppliedKey,
      });
      return;
    }
    loadPeriodSummary(scopeMode);
  }, [scopeMode, customAppliedKey, sinceAppliedKey, loadPeriodSummary]);

  const handleScopeChange = useCallback((nextScope) => {
    setScopeMode(nextScope);
    if (nextScope === DETAIL_SCOPE.SINCE_DAY) {
      setSinceAppliedKey(sinceStartDate);
    } else {
      setSinceAppliedKey("");
    }
    if (nextScope !== DETAIL_SCOPE.CUSTOM) {
      setCustomAppliedKey("");
    }
  }, [sinceStartDate]);

  const handleSinceStartDateChange = useCallback(
    (value) => {
      setSinceStartDate(value);
      writeStoredSinceStartDay(farmId, value);
      if (scopeMode === DETAIL_SCOPE.SINCE_DAY && value) {
        setSinceAppliedKey(value);
      }
    },
    [farmId, scopeMode]
  );

  const handleSinceDayApply = useCallback(() => {
    if (!sinceStartDate) {
      setPeriodError(t("Informe a data inicial."));
      return;
    }
    writeStoredSinceStartDay(farmId, sinceStartDate);
    setScopeMode(DETAIL_SCOPE.SINCE_DAY);
    setPeriodError(null);
    setSinceAppliedKey(sinceStartDate);
  }, [farmId, sinceStartDate, t]);

  const handleApplyCustom = useCallback(() => {
    if (!customDateFrom || !customDateTo || customDateFrom > customDateTo) {
      setPeriodError(t("Informe um intervalo de datas válido."));
      return;
    }
    setPeriodError(null);
    setCustomAppliedKey(`${customDateFrom}|${customDateTo}`);
  }, [customDateFrom, customDateTo, t]);

  const display = useMemo(() => {
    if (!selection) return null;

    const { lane, interval, startMs, endMs } = selection;
    const isMachine = lane.type === "machine";
    const isIntervalScope = scopeMode === DETAIL_SCOPE.INTERVAL;

    if (isIntervalScope || !isMachine) {
      const sessionDetail = interval.sessionDetail || null;
      const machineFuel = fuelRecords.filter(
        (row) => row.machineIdent === lane.label
      );
      const fuel = fuelStatsForRange(machineFuel, startMs, endMs);

      return {
        isMachine,
        isIntervalScope: true,
        startMs,
        endMs,
        meterType:
          (sessionDetail && sessionDetail.meterType) || null,
        sessionDetail,
        comparison:
          sessionDetail && sessionDetail.telemetryComparison
            ? sessionDetail.telemetryComparison
            : null,
        fuel,
        sessionCount: null,
        maintenances:
          (sessionDetail && sessionDetail.maintenances) || [],
        panelPhotoUrl:
          sessionDetail && sessionDetail.panelPhotoUrl
            ? sessionDetail.panelPhotoUrl
            : null,
      };
    }

    const summary = periodSummary || {};
    return {
      isMachine: true,
      isIntervalScope: false,
      startMs: summary.dateFrom
        ? new Date(summary.dateFrom).getTime()
        : startMs,
      endMs: summary.dateTo ? new Date(summary.dateTo).getTime() : endMs,
      meterType: summary.meterType || null,
      sessionDetail: {
        workedHours: summary.totalWorkedHours,
        workedKm: summary.totalWorkedKm,
        workedKmFromGps: summary.workedKmFromGps,
        workedHectares: summary.totalWorkedHectares,
        assume: null,
        finish: null,
      },
      comparison: summary.telemetryComparison || null,
      fuel: summary.fuel || {
        totalLiters: 0,
        recordCount: 0,
        avgLitersPerStop: 0,
      },
      sessionCount: summary.sessionCount || 0,
      maintenances: summary.maintenances || [],
      panelPhotoUrl: null,
    };
  }, [selection, scopeMode, periodSummary, fuelRecords]);

  if (!selection || !display) return null;

  const { lane } = selection;
  const {
    isMachine,
    isIntervalScope,
    startMs,
    endMs,
    meterType,
    sessionDetail,
    comparison,
    fuel,
    sessionCount,
    maintenances,
    panelPhotoUrl,
  } = display;

  const meterPresentation = resolveMeterPresentation(meterType, t);
  const displayedPanelKm = resolveDisplayedPanelKm({
    workedKm: sessionDetail && sessionDetail.workedKm,
    workedKmFromGps: sessionDetail && sessionDetail.workedKmFromGps,
    pathDistanceKm: comparison && comparison.pathDistanceKm,
    meterPresentation,
    t,
  });

  return (
    <div className="field-app-activity-detail field-app-activity-detail--inline field-app-activity-detail--session">
      <div className="field-app-activity-detail-header">
        <div className="field-app-activity-detail-title">
          {lane.type === "telemetry"
            ? t("Telemetria ativa")
            : t("Máquina {{ident}}", { ident: lane.label })}
        </div>
        <button
          type="button"
          className="btn btn-xs btn-default"
          onClick={onClose}
        >
          {t("Fechar")}
        </button>
      </div>

      {isMachine ? (
        <DetailScopeToolbar
          scopeMode={scopeMode}
          onScopeChange={handleScopeChange}
          sinceStartDate={sinceStartDate}
          onSinceStartDateChange={handleSinceStartDateChange}
          onSinceDayApply={handleSinceDayApply}
          customDateFrom={customDateFrom}
          customDateTo={customDateTo}
          onCustomDateFromChange={setCustomDateFrom}
          onCustomDateToChange={setCustomDateTo}
          onApplyCustom={handleApplyCustom}
          t={t}
        />
      ) : null}

      {periodLoading ? (
        <div className="field-app-activity-session-loading">
          <MDSpinner singleColor="#1ABB9C" size={28} />
          <span>{t("Carregando resumo do período...")}</span>
        </div>
      ) : null}

      {periodError ? (
        <p className="field-app-activity-session-error">{periodError}</p>
      ) : null}

      {isMachine && sessionDetail ? (
        <MachineClosureEditor
          key={`${selectionKey}-${closureSavedToken}`}
          sessionDetail={sessionDetail}
          farmId={farmId}
          formatTimestamp={formatTimestamp}
          t={t}
          onSaved={() => setClosureSavedToken((value) => value + 1)}
          chartPickActive={chartPickActive}
          onStartChartPick={onStartChartPick}
          onStopChartPick={onStopChartPick}
          selectedChartPoint={selectedChartPoint}
          onClearChartPoint={onClearChartPoint}
        />
      ) : null}

      <div className="field-app-activity-session-columns">
        <SessionColumn
          title={
            isIntervalScope ? t("Período no gráfico") : t("Período analisado")
          }
          hint={
            isIntervalScope
              ? t("Dados somente deste trecho clicado na linha.")
              : t("Totais agregados de todos os turnos no intervalo escolhido.")
          }
        >
          <MetricRow
            label={t("Início")}
            value={formatTimestamp(startMs)}
          />
          <MetricRow label={t("Fim")} value={formatTimestamp(endMs)} />
          <MetricRow
            label={t("Duração")}
            value={formatActivityDuration(endMs - startMs)}
          />
          {!isIntervalScope && sessionCount != null ? (
            <MetricRow
              label={t("Turnos no período")}
              value={String(sessionCount)}
              emphasize
            />
          ) : null}
          {isIntervalScope && selection.interval.endEvent ? (
            <MetricRow
              label={t("Encerramento")}
              value={eventLabel(selection.interval.endEvent)}
            />
          ) : null}
        </SessionColumn>

        {isMachine && sessionDetail ? (
          <>
            <SessionColumn
              title={t("Painel da máquina")}
              hint={
                isIntervalScope
                  ? t("Leituras registradas ao assumir e finalizar no tablet.")
                  : meterPresentation.panelColumnHint
              }
            >
              {meterPresentation.usesHours &&
              sessionDetail.workedHours != null ? (
                <MetricRow
                  label={meterPresentation.panelWorkedLabel}
                  value={formatHours(sessionDetail.workedHours)}
                  emphasize
                  info={meterPresentation.panelWorkedHint}
                />
              ) : null}
              {displayedPanelKm ? (
                <MetricRow
                  label={displayedPanelKm.label}
                  value={formatKm(displayedPanelKm.value)}
                  emphasize
                  info={displayedPanelKm.hint}
                />
              ) : null}
              {sessionDetail.workedHectares != null ? (
                <MetricRow
                  label={t("Hectares no painel da máquina")}
                  value={`${formatNumber(sessionDetail.workedHectares)} ha`}
                  emphasize
                />
              ) : null}
              {isIntervalScope && sessionDetail.assume ? (
                <>
                  <p className="field-app-activity-session-subtitle">
                    {t("Ao assumir")}
                  </p>
                  <SessionSnapshot
                    snapshot={sessionDetail.assume}
                    formatTimestamp={formatTimestamp}
                    t={t}
                    compact
                    meterPresentation={meterPresentation}
                  />
                </>
              ) : null}
              {isIntervalScope && sessionDetail.finish ? (
                <>
                  <p className="field-app-activity-session-subtitle">
                    {t("Ao finalizar")}
                  </p>
                  <SessionSnapshot
                    snapshot={sessionDetail.finish}
                    formatTimestamp={formatTimestamp}
                    t={t}
                    compact
                    meterPresentation={meterPresentation}
                  />
                </>
              ) : null}
            </SessionColumn>

            <TelemetryComparisonColumn
              comparison={comparison}
              farmId={farmId}
              operationId={operationId}
              isIntervalScope={isIntervalScope}
              meterPresentation={meterPresentation}
              t={t}
            />

            <FuelColumn fuel={fuel} t={t} />

            {maintenances.length ? (
              <SessionColumn
                title={
                  isIntervalScope
                    ? t("Manutenções no turno")
                    : t("Manutenções no período")
                }
              >
                <div className="field-app-activity-session-maintenances">
                  {maintenances.map((item, index) => (
                    <MaintenanceCard
                      key={`${item.timestamp}-${index}`}
                      item={item}
                      formatTimestamp={formatTimestamp}
                      t={t}
                    />
                  ))}
                </div>
              </SessionColumn>
            ) : null}
          </>
        ) : isMachine ? (
          <SessionColumn title={t("Painel da máquina")}>
            <p className="field-app-activity-session-empty">
              {t(
                "Sem dados detalhados de assumir/finalizar sincronizados para este trecho."
              )}
            </p>
          </SessionColumn>
        ) : null}

        {isMachine && isIntervalScope && panelPhotoUrl ? (
          <SessionColumn
            title={t("Foto do painel")}
            className="field-app-activity-session-column--photo"
          >
            <p className="field-app-detail-photo-hint">
              {t(
                "Imagem enviada pelo operador ao assumir a máquina no tablet."
              )}
            </p>
            <a
              href={panelPhotoUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="field-app-detail-photo-link"
            >
              <img
                src={panelPhotoUrl}
                alt={t("Foto do painel da máquina")}
                className="field-app-detail-photo field-app-activity-session-photo"
              />
            </a>
          </SessionColumn>
        ) : null}
      </div>
    </div>
  );
}
